import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import {
  getMyGhlAccount,
  saveMyGhlAccount,
  disconnectMyGhlAccount,
} from "@/lib/ghl-connect.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Loader2, Trash2 } from "lucide-react";

export const Route = createFileRoute("/admin/social/connect")({
  component: ConnectPage,
  ssr: false,
  head: () => ({ meta: [{ title: "Connect GHL · Social" }] }),
});

function ConnectPage() {
  const get = useServerFn(getMyGhlAccount);
  const save = useServerFn(saveMyGhlAccount);
  const disconnect = useServerFn(disconnectMyGhlAccount);

  const q = useQuery({ queryKey: ["my-ghl-account"], queryFn: () => get() });

  const [locationId, setLocationId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [label, setLabel] = useState("");
  const [ghlUserId, setGhlUserId] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (q.data?.account) {
      setLocationId(q.data.account.location_id ?? "");
      setLabel(q.data.account.label ?? "");
      setGhlUserId(q.data.account.ghl_user_id ?? "");
    }
  }, [q.data]);

  const account = q.data?.account;

  const onSave = async () => {
    if (!locationId.trim()) {
      toast.error("Location ID is required");
      return;
    }
    if (!account && !apiKey.trim()) {
      toast.error("Private Integration Token is required for the first connection");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await save({
        data: {
          location_id: locationId.trim(),
          api_key: apiKey.trim() || undefined,
          label: label.trim() || undefined,
          ghl_user_id: ghlUserId.trim() || undefined,
        },
      });
      if (res.ok) {
        toast.success("GoHighLevel connection saved and verified");
        setApiKey("");
        await q.refetch();
      } else {
        toast.warning("Connection saved, but verification needs attention");
        await q.refetch();
      }
    } catch (e: any) {
      const message = e?.message ?? "Failed to save the GoHighLevel connection";
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const onDisconnect = async () => {
    if (!confirm("Disconnect your GHL account? Your posts will stop publishing.")) return;
    await disconnect();
    setLocationId("");
    setApiKey("");
    setLabel("");
    setGhlUserId("");
    toast.success("Disconnected");
    q.refetch();
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Connect your GoHighLevel account</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Paste your GHL <strong>Location ID</strong> and a <strong>Private Integration Token</strong> (PIT)
          with Social Media permissions. We'll save and test the connection — then your Calendar,
          Evergreen, Compose, and Analytics tabs all run through this account.
        </p>
      </div>

      <ol className="space-y-3 rounded-lg border border-border bg-muted/30 p-5 text-sm">
        <li className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
            1
          </span>
          <div>
            <p className="font-semibold text-foreground">Copy your Location ID</p>
            <p className="text-muted-foreground">
              In GHL go to <strong>Settings → Business Profile</strong> and copy the{" "}
              <strong>Location ID</strong>. Paste it below.
            </p>
          </div>
        </li>
        <li className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
            2
          </span>
          <div>
            <p className="font-semibold text-foreground">Create a Private Integration Token (PIT)</p>
            <p className="text-muted-foreground">
              In GHL go to <strong>Settings → Private Integrations → Create new</strong>. Enable these
              required scopes, then copy the token and paste it below:
            </p>
            <ul className="mt-1 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
              {[
                "socialplanner/oauth.readonly",
                "socialplanner/oauth.write",
                "socialplanner/post.readonly",
                "socialplanner/post.write",
                "socialplanner/account.readonly",
                "socialplanner/account.write",
                "socialplanner/csv.readonly",
                "socialplanner/csv.write",
                "socialplanner/category.readonly",
                "socialplanner/category.write",
                "socialplanner/statistics.readonly",
                "socialplanner/tag.readonly",
                "socialplanner/tag.write",
              ].map((scope) => (
                <li key={scope}>
                  <code className="rounded bg-background px-1 py-0.5">{scope}</code>
                </li>
              ))}
            </ul>
          </div>
        </li>
        <li className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
            3
          </span>
          <div>
            <p className="font-semibold text-foreground">Test &amp; save</p>
            <p className="text-muted-foreground">
              Hit <strong>Test &amp; save</strong>. A green banner means you're connected and the
              planner is ready to use.
            </p>
          </div>
        </li>
      </ol>

      {account && (
        <div
          className={`flex items-start gap-3 rounded-lg border p-4 ${
            account.last_test_ok
              ? "border-emerald-500/30 bg-emerald-500/10"
              : "border-destructive/40 bg-destructive/10"
          }`}
        >
          {account.last_test_ok ? (
            <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-500" />
          ) : (
            <AlertCircle className="mt-0.5 h-5 w-5 text-destructive" />
          )}
          <div className="flex-1 text-sm">
            <p className="font-semibold">
              {account.last_test_ok ? "Connected" : "Connected, but last test failed"}
              {account.label ? ` — ${account.label}` : ""}
            </p>
            <p className="text-muted-foreground">Location: {account.location_id}</p>
            {account.last_test_error && (
              <p className="mt-1 text-destructive">{account.last_test_error}</p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onDisconnect}>
            <Trash2 className="mr-1 h-4 w-4" /> Disconnect
          </Button>
        </div>
      )}

      <div className="space-y-4 rounded-lg border border-border bg-card p-5">
        {saveError && (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            <p className="font-semibold">Connection could not be saved</p>
            <p className="mt-1">{saveError}</p>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="label">Label (optional)</Label>
          <Input
            id="label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="My GHL sub-account"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="loc">Location ID</Label>
          <Input
            id="loc"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            placeholder="ve9EPM428h8vShlRW1KT"
          />
          <p className="text-xs text-muted-foreground">
            Find it in GHL → Settings → Business Profile.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="key">Private Integration Token</Label>
          <Input
            id="key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={account ? "•••••• (leave blank to keep existing)" : "pit-xxxxxxxx..."}
          />
          <p className="text-xs text-muted-foreground">
            Create one in GHL → Settings → Private Integrations and enable all the{" "}
            <code>socialplanner/*</code> scopes listed in step 2 above.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="uid">GHL User ID (optional)</Label>
          <Input
            id="uid"
            value={ghlUserId}
            onChange={(e) => setGhlUserId(e.target.value)}
            placeholder="Used as the author for posts"
          />
        </div>
        <div className="flex gap-2">
          <Button type="button" onClick={onSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Test & save
          </Button>
        </div>
      </div>
    </div>
  );
}
